import { useCallback, useMemo, useState } from 'react';
import { Button } from '@components/button';
import { useDeleteDocument, useSearchDocuments } from '@services/document';
import { DocumentFieldNames, IconTypes } from '@pages/PolicyDetails/PolicyDetails.constants';
import { EllipsisH, FileUpload } from '@assets/svgs';
import { Document, DocumentStatus, DocumentType } from '@models/document';
import { MenuButton, MenuItemType } from '@components/menu';
import { useStore } from '@store/GlobalStore';
import { useParams } from 'react-router-dom';
import { OverflowTooltip } from '@components/tooltip/OverflowTooltip';
import { shallow } from 'zustand/shallow';
import { EditDocumentDialog } from '@pages/PolicyDetails/dialogs/EditDocumentDialog';
import { Table } from '@components/table/Table';
import { DocumentSearchResponse } from '@lio/api-types';
import * as styles from '@components/dataGrid/DataGrid.styles';
import { TableFilter } from '@components/table/types';
import { Box } from '@mui/material';
import { TableGridBaseColDef, TableGridRenderCellParams } from '@components/table/types/TableColumnDef.type';
import { RegenerateTransactionDocumentDialog } from '../dialogs';
import { UploadDocumentDialog } from '../dialogs/UploadDocumentDialog';
import { ClockFormat, documentTypeTaxonomy, documentTypeTaxonomyById } from '@lio/rule-engine';
import type { DocumentTypeTaxonomyEntry } from '@lio/rule-engine';
import { getDateFormatter, LongDateFormatWithoutSeconds } from '@utils/date';
import { format } from 'date-fns-tz';
import { useGetPolicy } from '@services/policy';

const SOURCE_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'generated', label: 'Generated' },
  { value: 'manual', label: 'Manual' },
];

// Document types that can appear as generated documents (have a documentGeneration record).
// The Binder / Confirmation of Coverage entry is omitted here and added dynamically based on
// writing company, so both can share the same position in the list.
const GENERATED_DOC_TYPES_BASE = [
  DocumentType.CertificateOfInsurance,
  DocumentType.Cancellation,
  DocumentType.Endorsement,
  DocumentType.Policy,
  DocumentType.Rewrite,
  DocumentType.Application,
  DocumentType.LossHistoryReport,
  DocumentType.SurplusLinesFilingConfirmation,
  DocumentType.NoticeOfIntentToCancel,
  DocumentType.NoticeOfCancellation,
  DocumentType.NoticeOfConditionalRenewal,
  DocumentType.NoticeOfNonrenewal,
  DocumentType.NoticeOfRescissionOfPendingNonrenewal,
  DocumentType.NoticeOfRescissionOfPendingCancellation,
  DocumentType.FraudStatementDisclosure,
] as const;

// Canonical display names for document type IDs. Used to override taxonomy display names (which
// can differ from the API-returned documentType.name) for consistency with the table's Document
// column.
const DOC_TYPE_LABEL_BY_ID: Record<string, string> = Object.values(DocumentType).reduce(
  (acc, dt) => ({ ...acc, [dt.id]: dt.name }),
  {} as Record<string, string>,
);

// Returns true if the document type should be visible for the given role. Types not present in
// the taxonomy are legacy types that pre-date the taxonomy; treat them as visible to everyone to
// preserve existing behaviour.
const isDocTypeVisibleForRole = (id: string, isInternalUser: boolean): boolean => {
  const entry = documentTypeTaxonomyById[id];
  if (!entry) return true;
  return isInternalUser ? entry.internalVisible : entry.brokerVisible;
};

const toDocTypeOption = (dt: { id: string; name: string }) => ({ label: dt.name, value: dt.id });

const getGeneratedOptions = (generatedDocTypes: Array<{ id: string; name: string }>, isInternalUser: boolean) =>
  generatedDocTypes.filter((dt) => isDocTypeVisibleForRole(dt.id, isInternalUser)).map(toDocTypeOption);

const getManualOptions = (isInternalUser: boolean) => {
  const entries = (documentTypeTaxonomy as DocumentTypeTaxonomyEntry[]).filter((e) => {
    const canUpload = isInternalUser ? e.internalUpload : e.brokerUpload;
    const isVisible = isInternalUser ? e.internalVisible : e.brokerVisible;
    return canUpload && isVisible;
  });
  const legacyTypes = [DocumentType.Other].filter((dt) => !entries.some((e) => e.id === dt.id));
  return [
    ...entries.map((e) => ({ label: DOC_TYPE_LABEL_BY_ID[e.id] ?? e.displayName, value: e.id })),
    ...legacyTypes.map(toDocTypeOption),
  ].sort((a, b) => a.label.localeCompare(b.label));
};

const getPolicyDetailsDocumentsColumns = ({
  isInternalUser,
  clockFormat,
}: {
  isInternalUser: boolean;
  clockFormat: ClockFormat;
}): TableGridBaseColDef[] => {
  return [
    {
      field: DocumentFieldNames.Document,
      headerName: 'Document',
      flex: 1,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => {
        const { file, documentGeneration, documentType } = params.row;
        const isManuallyUploaded = !documentGeneration;
        const hasFileDownload =
          !!file?.downloadUrl && (isManuallyUploaded || documentGeneration?.status === DocumentStatus.Completed);
        const icon = documentGeneration?.status ? IconTypes[documentGeneration?.status] : null;
        return (
          <Button
            variant='text'
            href={hasFileDownload ? file?.downloadUrl : undefined}
            disabled={!hasFileDownload}
            sx={{
              padding: '0',
              '&.Mui-disabled': {
                fontWeight: '400',
                cursor: 'default',
                color: 'text.subdued',
              },
            }}
            endIcon={icon}
          >
            <OverflowTooltip tooltipText={documentType.name}>{documentType.name}</OverflowTooltip>
          </Button>
        );
      },
    },
    {
      field: DocumentFieldNames.FileName,
      headerName: 'Filename',
      flex: 1,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => <OverflowTooltip>{params.row.file?.fileName}</OverflowTooltip>,
    },
    {
      field: DocumentFieldNames.Date,
      headerName: 'Date',
      flex: 1,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => (
        <OverflowTooltip>
          {format(new Date(params.row.createdAt), getDateFormatter(clockFormat, LongDateFormatWithoutSeconds))}
        </OverflowTooltip>
      ),
    },
    {
      field: DocumentFieldNames.Summary,
      headerName: 'Summary',
      flex: 1,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => (
        <OverflowTooltip>{params.row.file?.description ?? '-'}</OverflowTooltip>
      ),
    },
    {
      field: DocumentFieldNames.Menu,
      headerName: '',
      flex: 0,
      sortable: false,
      align: 'right',
      renderCell: (params: TableGridRenderCellParams) => {
        const isManuallyUploaded = !params.row.documentGeneration;

        if (isManuallyUploaded) {
          const menuItems = [
            {
              type: MenuItemType.Default,
              text: 'Delete document',
              action: () => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { handleDeleteDocument, ...document } = params.row;
                params.row.handleDeleteDocument(document);
              },
            },
          ];
          return isInternalUser ? <MenuButton items={menuItems} icon={<EllipsisH />} /> : null;
        }

        const menuItems = [
          {
            type: MenuItemType.Default,
            text: 'Regenerate transaction document',
            action: () => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { handleRegenerateTransactionDocument, ...document } = params.row;
              params.row.handleRegenerateTransactionDocument(document);
            },
          },
          {
            type: MenuItemType.Default,
            text: 'Delete document',
            action: () => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { handleDeleteDocument, ...document } = params.row;
              params.row.handleDeleteDocument(document);
            },
          },
        ];
        if (
          params.row.documentType.id === DocumentType.CertificateOfInsurance.id &&
          params.row?.documentGeneration?.status === DocumentStatus.Completed
        ) {
          menuItems.push({
            type: MenuItemType.Default,
            text: 'Edit Certificate',
            action: () => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { handleRegenerateTransactionDocument, ...document } = params.row;
              params.row.handleEditDocument(document);
            },
          });
        }
        return isInternalUser ? <MenuButton items={menuItems} icon={<EllipsisH />} /> : null;
      },
    },
  ];
};

export const PolicyDocumentsTab = () => {
  const { policyId } = useParams();
  const { policy } = useGetPolicy({ policyId });
  const [{ isInternalUser }, layoutPreferences] = useStore(
    (state) => [state.userAuth, state.layoutPreferences],
    shallow,
  );
  const clockFormat = layoutPreferences.clockFormat;
  const { deleteDocumentAsync } = useDeleteDocument();
  const [selectedDocument, setSelectedDocument] = useState<Document | null>();
  const [selectedRegenerateDocument, setSelectedRegenerateDocument] = useState<Document | null>();
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('all');
  // Tracks the currently selected Document Type filter value so we can clear it programmatically
  // when the source pill changes and the selected type is no longer valid for the new source.
  const [documentTypeFilter, setDocumentTypeFilter] = useState<Record<string, string> | null>(null);

  const handleEditDocument = useCallback(
    (d: Document) => {
      setSelectedDocument(d);
    },
    [setSelectedDocument],
  );

  const handleRegenerateTransactionDocument = useCallback(
    (d: Document) => {
      setSelectedRegenerateDocument(d);
    },
    [setSelectedRegenerateDocument],
  );

  const handleDeleteDocument = useCallback(
    async (el: Document) => {
      await deleteDocumentAsync({ documentId: el.id });
    },
    [deleteDocumentAsync],
  );

  const isWritingCompanyLio = useMemo(() => policy?.boundQuote?.writingCompany.productCode === 'LIO', [policy]);

  const formatRows = useCallback(
    (data: DocumentSearchResponse['items']) => {
      if (!data) return [];

      const filtered = data.filter((document) => {
        if (sourceFilter === 'manual') return !document.documentGeneration;
        if (sourceFilter === 'generated') return !!document.documentGeneration;
        return true;
      });

      return filtered.map((document) => {
        if (isWritingCompanyLio && document.documentType.id === DocumentType.ConfirmationOfCoverage.id) {
          return {
            ...document,
            documentType: {
              ...document.documentType,
              name: DocumentType.ConfirmationOfCoverage.name,
            },
            handleRegenerateTransactionDocument,
            handleEditDocument,
            handleDeleteDocument,
          };
        }

        return {
          ...document,
          handleRegenerateTransactionDocument,
          handleEditDocument,
          handleDeleteDocument,
        };
      });
    },
    [handleEditDocument, handleRegenerateTransactionDocument, handleDeleteDocument, isWritingCompanyLio, sourceFilter],
  );

  const columns = useMemo(() => {
    return getPolicyDetailsDocumentsColumns({ isInternalUser, clockFormat });
  }, [isInternalUser, clockFormat]);

  // Full generated doc types list, including the writing-company-dependent Binder/CoC entry.
  const generatedDocTypes = useMemo(
    () => [
      isWritingCompanyLio ? DocumentType.ConfirmationOfCoverage : DocumentType.Binder,
      ...GENERATED_DOC_TYPES_BASE,
    ],
    [isWritingCompanyLio],
  );

  // Options to show in the Document Type dropdown, filtered by the active source pill and role.
  const docTypeOptions = useMemo(() => {
    if (sourceFilter === 'generated') return getGeneratedOptions(generatedDocTypes, isInternalUser);
    if (sourceFilter === 'manual') return getManualOptions(isInternalUser);

    // 'all' — union of generated + manual types (deduped), keeping generated types first.
    const generatedOptions = getGeneratedOptions(generatedDocTypes, isInternalUser);
    const generatedIds = new Set(generatedOptions.map((o) => o.value));
    const extraManualOptions = getManualOptions(isInternalUser).filter((o) => !generatedIds.has(o.value));
    return [...generatedOptions, ...extraManualOptions];
  }, [sourceFilter, isInternalUser, generatedDocTypes]);

  // Passed to the Table so it can clear the Document Type filter when the source changes and the
  // currently selected type is no longer a valid option.
  const tableCustomFilters = useMemo(() => ({ documentType: documentTypeFilter }), [documentTypeFilter]);

  const onFilterChange = useCallback(
    (key: string, value: any) => {
      if (key === 'source') {
        const newSource = value ?? 'all';
        setSourceFilter(newSource);

        // If the user has a Document Type selected, check whether it is still valid for the new
        // source. If not, clear it so the user isn't filtering by an invisible option.
        if (documentTypeFilter?.value && newSource !== 'all') {
          const selectedId = documentTypeFilter.value;

          const stillValid = (() => {
            if (newSource === 'generated') {
              return getGeneratedOptions(generatedDocTypes, isInternalUser).some((o) => o.value === selectedId);
            }
            if (newSource === 'manual') {
              return getManualOptions(isInternalUser).some((o) => o.value === selectedId);
            }
            return true;
          })();

          if (!stillValid) setDocumentTypeFilter(null);
        }
      }

      if (key === 'documentType') {
        setDocumentTypeFilter(value ?? null);
      }
    },
    [documentTypeFilter, generatedDocTypes, isInternalUser],
  );

  const documentFilters = useMemo(() => {
    const filters: NonNullable<Parameters<typeof Table>[0]['filterConfig']>['filters'] = [
      {
        field: 'documentType',
        label: 'Document Type',
        width: { setWidth: 350 },
        type: TableFilter.SELECT_SINGLE,
        other: {
          options: docTypeOptions,
        },
      },
    ];

    if (isInternalUser) {
      filters.push({
        field: 'source',
        width: { gridWidth: { xs: 12 } },
        type: TableFilter.CHIP_GROUP,
        other: {
          options: SOURCE_FILTER_OPTIONS,
          sxProps: { alignItems: 'center' },
        },
      });
    }

    return filters;
  }, [docTypeOptions, isInternalUser]);

  return (
    <>
      <EditDocumentDialog document={selectedDocument ?? null} onClose={() => setSelectedDocument(null)} />
      {policyId && selectedRegenerateDocument && (
        <RegenerateTransactionDocumentDialog
          open={!!selectedRegenerateDocument}
          policyId={policyId}
          document={selectedRegenerateDocument}
          handleCloseDialog={() => setSelectedRegenerateDocument(null)}
        />
      )}
      {policyId && (
        <UploadDocumentDialog
          open={showUploadDialog}
          policyId={policyId}
          isInternalUser={isInternalUser}
          onClose={() => setShowUploadDialog(false)}
        />
      )}
      <Box sx={{ position: 'relative', height: 'auto', width: '100%' }}>
        <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
          <Button variant='text' startIcon={<FileUpload />} onClick={() => setShowUploadDialog(true)}>
            Upload
          </Button>
        </Box>
        <Table
          ariaLabel='documents-tab-grid'
          onLoadData={useSearchDocuments as any}
          columns={columns}
          rowFormatter={formatRows}
          defaultQueryParams={{ policyId }}
          noResultsConfig={{ noResultsMessage: 'No documents found' }}
          filterConfig={{
            filters: documentFilters,
            filterQueryParam: {
              documentType: 'documentTypeId',
              source: () => null,
            },
            defaultFilters: { source: 'all' },
            customFilters: tableCustomFilters,
            onFilterChange,
          }}
          sxProps={{
            tableContainer: {
              ...styles.dataGridContainer,
              display: 'block',
              height: 'fit-content',
            },
            dataGridContainer: {
              ...styles.dataGridContainer,
              borderLeft: 'none',
              padding: 0,
              height: 'auto',
            },
            dataGrid: styles.dataGrid,
          }}
        />
      </Box>
    </>
  );
};
